import {
  App,
  CurrentUserResponse,
  DeviceLoginCredentials,
  InviteMemberRequest,
  InviteMemberResponse,
  ResendInvitationResponse,
  Lens,
  Organization,
  OrganizationUsersListResponse,
  Permission,
  ProcessorListResponse,
  Role,
  Sensor,
  SensorListResponse,
  SensorScope,
  Subscription,
  Tag,
  TagListResponse,
  CreatedTemplateProcessor,
  AttachableTemplate,
  TemplateProcessor,
  TemplateProcessorPushCredentials,
  TemplateProcessorVersion,
  CreatedTemplateProcessorVersion,
  UserRoleAssignment,
} from '../types/api';
import { applyBatchWindow } from '../lib/listPage';

type BackendAppClient = {
  id: string;
  organizationId: string;
  appClientId: string;
  clientName: string | null;
  clientKind?: 'app' | 'template_publisher';
  subscriptionCount?: number;
  processorTemplateId?: string | null;
  clientSecret: string | null;
  authenticationFlowSessionDuration: number | null;
  refreshTokenExpiration: number | null;
  accessTokenExpiration: number | null;
  idTokenExpiration: number | null;
  enableTokenRevocation: boolean | null;
  appName: string | null;
  isActive: boolean;
  /** True when the API could not reach Cognito for this row. */
  detailsUnavailable?: boolean;
  createdAt: string;
  updatedAt: string;
};

/**
 * Thrown for every non-2xx response this client receives.
 *
 * The message is exactly what this client has always thrown (the server's
 * `error` field, else the raw body, else `HTTP error! status: <n>`), so
 * `humanizeError` still produces the same copy. What is new is `status`:
 * callers that need to treat one status specially — swallowing a 403 rather
 * than painting a global banner — can branch on the number instead of
 * pattern-matching the server's English prose.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The API's machine-readable refusal code (`SLUG_TAKEN`, `BROKER_UNAVAILABLE`),
   * when the body carried one. lib/errors.ts reads it before the status.
   */
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    // Subclassing Error loses the prototype chain once the class is
    // down-levelled (tsconfig targets es5), which would make `instanceof
    // ApiError` — and `instanceof Error` in humanizeError — false. Restore it.
    Object.setPrototypeOf(this, ApiError.prototype);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Every request in this client aborts itself after this long.
 *
 * The browser's fetch has no timeout of its own. A request that never settles
 * — a dropped Wi-Fi connection, a laptop waking from sleep — leaves the
 * caller's loading flag raised forever: grey skeletons, no error, and no retry
 * button, because nothing ever rejects. AWS normally hangs up well inside this
 * window (Lambda and API Gateway both time out first), so the ceiling only
 * fires when the network on this end has gone away.
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * Thrown when a request hits REQUEST_TIMEOUT_MS.
 *
 * Deliberately *not* an AbortError. `useInfiniteList` and the page fetchers
 * swallow AbortError without touching state — a superseded request is not a
 * failure — so a timeout raised as one would reproduce the exact hang it
 * exists to end. The message leads with "NetworkError" because that is the
 * phrasing `humanizeError`'s first pattern matches, and "Could not reach the
 * server. Check your connection and try again." is precisely what happened.
 */
export class ApiTimeoutError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`NetworkError: the request timed out after ${Math.round(timeoutMs / 1000)}s`);
    Object.setPrototypeOf(this, ApiTimeoutError.prototype);
    this.name = 'ApiTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/**
 * The argument guard that used to be spelled out, six lines at a time, in
 * every method. Same messages, and still a plain Error with no status: these
 * are programmer mistakes (a caller forgot the organization id), never
 * something the server said.
 */
function requireArg(value: unknown, message: string): void {
  if (!value) {
    throw new Error(message);
  }
}

const mapBackendAppClientToApp = (appClient: BackendAppClient): App => ({
  id: appClient.id,
  name: appClient.appName || appClient.clientName || appClient.appClientId,
  createdAt: appClient.createdAt,
  updatedAt: appClient.updatedAt,
  clientId: appClient.appClientId,
  clientSecret: appClient.clientSecret || '',
  clientKind: appClient.clientKind ?? 'app',
  subscriptionCount: appClient.subscriptionCount,
  processorTemplateId: appClient.processorTemplateId ?? null,
  authFlowSessionDuration: appClient.authenticationFlowSessionDuration ?? undefined,
  refreshTokenExpiration: appClient.refreshTokenExpiration ?? undefined,
  accessTokenExpiration: appClient.accessTokenExpiration ?? undefined,
  idTokenExpiration: appClient.idTokenExpiration ?? undefined,
  enableTokenRevocation: appClient.enableTokenRevocation ?? undefined,
  detailsUnavailable: appClient.detailsUnavailable ?? false,
});

/**
 * API Service - Singleton pattern for making API calls
 * Currently uses mock data, but can be easily replaced with real API calls
 */
class ApiService {
  private static instance: ApiService;
  private apiBaseUrl: string;

  private constructor() {
    // Private constructor to enforce singleton pattern.
    // The API URL is baked in at build time from VITE_API_URL.
    const envUrl = import.meta.env.VITE_API_URL;
    this.apiBaseUrl = envUrl || 'https://your-api-gateway-url.execute-api.us-east-2.amazonaws.com/prod';

    // Only the misconfiguration is worth a console line; a healthy load is not.
    if (!envUrl) {
      console.warn('[API Service] ⚠️ VITE_API_URL not set! Using placeholder URL:', this.apiBaseUrl);
      console.warn('[API Service] ⚠️ Please set VITE_API_URL in .env file or environment variables');
    }
  }
  
  /**
   * Get the current API base URL (for debugging)
   */
  getApiBaseUrl(): string {
    return this.apiBaseUrl;
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Retrieve app clients for a specific organization.
   */
  /**
   * Lists an organization's app clients.
   *
   * `includeCognitoDetails` costs the API one Cognito call per app client, so
   * it is opt-in and off by default. Only the My Apps table shows those fields
   * — token lifetimes and revocation — and only behind columns that are hidden
   * until a user turns them on. Everything else that reads this list (the
   * OrganizationContext load on sign-in and on every organization switch, the
   * request builder's app picker) wants names and ids, and must not pay for it.
   */
  async getOrganizationAppClients(
    organizationId: string,
    accessToken: string,
    options?: { includeCognitoDetails?: boolean; includeSubscriptionCounts?: boolean; signal?: AbortSignal },
  ): Promise<App[]> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params: Record<string, string> = { organizationId };
    if (options?.includeCognitoDetails) {
      params.includeCognitoDetails = 'true';
    }
    // One grouped query on the API's side, and a request of its own on this
    // side: the count is interesting but never worth delaying the list for.
    if (options?.includeSubscriptionCounts) {
      params.includeSubscriptionCounts = 'true';
    }

    const data = await this.fetchJson<{ apps?: BackendAppClient[] }>(this.url('/apps', params), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: options?.signal,
    });

    return (data.apps ?? []).map(mapBackendAppClientToApp);
  }

  /**
   * Create a new organization
   */
  async createOrganization(name: string, licenseKey: string, accessToken: string): Promise<Organization> {
    const trimmedName = name.trim();
    requireArg(trimmedName, 'Organization name is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{
      id: string;
      name: string;
      slug?: string;
      role?: string;
    }>(this.url('/portal/organizations'), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ name: trimmedName, licenseKey }),
    });

    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      apps: [],
    };
  }

  /**
   * Update app name.
   */
  async updateAppName(appId: string, newName: string, accessToken: string): Promise<App> {
    requireArg(appId, 'App ID is required');
    requireArg(newName.trim(), 'App name is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<BackendAppClient>(this.url(`/apps/${encodeURIComponent(appId)}`), {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ name: newName.trim() }),
    });
    return mapBackendAppClientToApp(data);
  }

  /**
   * Create a new app in an organization
   */
  async createApp(organizationId: string, appName: string, accessToken: string, scopes?: string[]): Promise<App> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(appName.trim(), 'App name is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<BackendAppClient>(this.url('/apps'), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        name: appName.trim(),
        organizationId,
        ...(Array.isArray(scopes) && scopes.length > 0 ? { scopes } : {}),
      }),
    });
    if (!data.appClientId || !data.id) {
      throw new Error('Invalid app creation response from API');
    }
    return mapBackendAppClientToApp(data);
  }

  /**
   * Get or create one template publisher app for a template slug.
   */
  /**
   * Provision publisher credentials for a template that has none.
   *
   * A one-time heal for templates created before credentials came with the
   * template; the API 409s a template that already has one.
   */
  async createTemplateCredentials(
    slug: string,
    organizationId: string,
    accessToken: string,
  ): Promise<{ clientId: string; clientSecret: string }> {
    requireArg(slug, 'Template slug is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ credentials: { clientId: string; clientSecret: string } }>(
      this.url(`/template-processors/${encodeURIComponent(slug)}/credentials`, { organizationId }),
      {
        method: 'POST',
        headers: this.authHeaders(accessToken),
        body: JSON.stringify({ organizationId }),
      },
    );
    if (!data.credentials?.clientId || !data.credentials?.clientSecret) {
      throw new Error('Invalid credentials response from API');
    }
    return data.credentials;
  }

  /**
   * Read one app client's id and secret.
   *
   * Deliberately a read: the secret lives in Cognito and is re-read per call,
   * so this may be invoked as many times as the user wants to copy it. It
   * replaces `createTemplatePublisherApp`, which POSTed /apps on every "view
   * credentials" click and therefore minted a new credential each time.
   */
  async getAppCredentials(
    appId: string,
    accessToken: string,
  ): Promise<{ appClientId: string; clientSecret: string; clientName: string | null }> {
    requireArg(appId, 'App ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{
      appClientId: string;
      clientSecret: string;
      clientName: string | null;
    }>(this.url(`/apps/${encodeURIComponent(appId)}/credentials`), {
      headers: this.authHeaders(accessToken),
    });

    if (!data.appClientId || !data.clientSecret) {
      throw new Error('Invalid credentials response from API');
    }
    return data;
  }

  /**
   * Delete an app.
   */
  async deleteApp(appId: string, accessToken: string): Promise<void> {
    requireArg(appId, 'App ID is required');
    requireArg(accessToken, 'Access token is required');

    await this.fetchJson<unknown>(this.url(`/apps/${encodeURIComponent(appId)}`), {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * Deletes a subscription: releases its processors and tears down its queue.
   *
   * The API has always allowed this for a portal user holding
   * `subscriptions:delete` — the ownership check applies only to machine
   * tokens — but nothing here called it, so the only route was the SDK, holding
   * the credentials of the client that created it. Losing those credentials
   * meant the subscription could never be removed, and the app client could
   * never be deleted either, because deletion refuses while subscriptions live.
   */
  async deleteSubscription(subscriptionId: string, accessToken: string): Promise<void> {
    requireArg(subscriptionId, 'Subscription ID is required');
    requireArg(accessToken, 'Access token is required');

    await this.fetchJson(this.url(`/subscriptions/${encodeURIComponent(subscriptionId)}`), {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * List active subscriptions for a specific app client.
   * Uses the internal app client UUID (App.id) as the lookup key.
   */
  async getAppClientSubscriptions(organizationId: string, appClientId: string, accessToken: string): Promise<Subscription[]> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(appClientId, 'App client ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ subscriptions?: Subscription[] }>(
      this.url('/subscriptions', { organizationId, appClientId }),
      {
        method: 'GET',
        headers: this.authHeaders(accessToken),
      },
    );
    return data.subscriptions ?? [];
  }

  async getSensors(
    organizationId: string,
    accessToken: string,
    options: {
      /** Batch window (useInfiniteList via batchFetcher). */
      limit?: number;
      offset?: number;
      cursor?: string;
      search: string;
      sortBy: 'name' | 'sensorType' | 'isActive' | 'isPublic' | 'lastSeenAt' | 'createdAt' | 'updatedAt';
      sortDirection: 'asc' | 'desc';
      /**
       * Which sensors the listing covers. Omitted means `own`, the tenant
       * boundary — the API defaults the same way, so leaving it out can never
       * widen a query by accident.
       */
      scope?: SensorScope;
      signal?: AbortSignal;
    }
  ): Promise<SensorListResponse> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({
      organizationId,
      search: options.search,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    });
    applyBatchWindow(params, options);
    if (options.scope && options.scope !== 'own') params.set('scope', options.scope);

    return this.fetchJson<SensorListResponse>(this.url('/sensors', params), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: options.signal,
    });
  }

  /**
   * `signal` is optional so the Request Builder can abort an in-flight load
   * when the organization switcher moves — without it, the previous
   * organization's sensors land on the new organization's map.
   */
  async getAllActiveSensors(accessToken: string, organizationId: string, signal?: AbortSignal): Promise<Sensor[]> {
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ sensors?: Sensor[] }>(this.url('/sensors/active', { organizationId }), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal,
    });
    return Array.isArray(data.sensors) ? data.sensors : [];
  }

  async getSensorTypes(accessToken: string, organizationId: string): Promise<string[]> {
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ sensorTypes?: string[] }>(this.url('/sensors/types', { organizationId }), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
    });
    return Array.isArray(data.sensorTypes) ? data.sensorTypes : [];
  }

  async createSensor(
    organizationId: string,
    payload: {
      name: string;
      description?: string | null;
      sensorType?: string;
      locationLat: number;
      locationLng: number;
      coverageGeom?: Record<string, unknown> | null;
      connectionConfig?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      isActive?: boolean;
      isPublic?: boolean;
      /** The device login to save with the sensor; absent or null saves none. Sent verbatim. */
      credentials?: DeviceLoginCredentials | null;
    },
    accessToken: string,
  ): Promise<Sensor> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(payload.name?.trim(), 'Sensor name is required');
    if (!Number.isFinite(payload.locationLat) || !Number.isFinite(payload.locationLng)) {
      throw new Error('Valid location latitude and longitude are required');
    }
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Sensor>(this.url('/sensors'), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        organizationId,
        ...payload,
      }),
    });
  }

  async deleteSensor(sensorId: string, organizationId: string, accessToken: string): Promise<void> {
    requireArg(sensorId, 'Sensor ID is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    await this.fetchJson<unknown>(
      this.url(`/sensors/${encodeURIComponent(sensorId)}`, { organizationId }),
      {
        method: 'DELETE',
        headers: this.authHeaders(accessToken),
      },
    );
  }

  async updateSensor(
    sensorId: string,
    organizationId: string,
    updates: {
      name?: string;
      description?: string | null;
      sensorType?: string;
      locationLat?: number;
      locationLng?: number;
      coverageGeom?: Record<string, unknown> | null;
      connectionConfig?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      isActive?: boolean;
      isPublic?: boolean;
      /**
       * The device login. Absent leaves it unchanged, null removes it, an
       * object replaces it. Sent verbatim, never trimmed.
       */
      credentials?: DeviceLoginCredentials | null;
    },
    accessToken: string,
  ): Promise<Sensor> {
    requireArg(sensorId, 'Sensor ID is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Sensor>(this.url(`/sensors/${encodeURIComponent(sensorId)}`), {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        organizationId,
        ...updates,
      }),
    });
  }

  async getProcessors(
    orgId: string,
    accessToken: string,
    options: {
      /** Batch window (useInfiniteList via batchFetcher). */
      limit?: number;
      offset?: number;
      cursor?: string;
      search: string;
      sortBy: 'status' | 'nodeType' | 'sensorId' | 'nodeIp' | 'k8sNamespace' | 'k8sPodName' | 'activeSubscriptionCount' | 'consecutiveFailures' | 'createdAt' | 'updatedAt';
      sortDirection: 'asc' | 'desc';
      /** Only these processor statuses (sent as a comma list); omitted = every status. */
      statuses?: readonly string[];
      /** Only processors whose sensor this organization owns. */
      organizationId?: string;
      signal?: AbortSignal;
    }
  ): Promise<ProcessorListResponse> {
    requireArg(orgId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({
      search: options.search,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    });
    if (options.statuses?.length) params.set('status', options.statuses.join(','));
    if (options.organizationId) params.set('organizationId', options.organizationId);
    applyBatchWindow(params, options);

    return this.fetchJson<ProcessorListResponse>(
      this.url(`/system/org/${encodeURIComponent(orgId)}/processors`, params),
      {
        headers: this.authHeaders(accessToken),
        signal: options.signal,
      },
    );
  }

  async getTags(
    organizationId: string,
    accessToken: string,
    options: {
      /** Batch window (useInfiniteList via batchFetcher). */
      limit?: number;
      offset?: number;
      cursor?: string;
      search: string;
      sortBy: 'id' | 'name' | 'slug' | 'description' | 'parentName' | 'tagType' | 'createdAt';
      sortDirection: 'asc' | 'desc';
      /** Narrow to these tags (a picker's selected rows outside the loaded batches). */
      ids?: string[];
      /** Leave out this tag and every tag beneath it (the Edit event type parent field). */
      excludeSubtreeOf?: string;
      signal?: AbortSignal;
    }
  ): Promise<TagListResponse> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({
      organizationId,
      search: options.search,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    });
    applyBatchWindow(params, options);
    if (options.ids?.length) params.set('ids', options.ids.join(','));
    if (options.excludeSubtreeOf) params.set('excludeSubtreeOf', options.excludeSubtreeOf);

    return this.fetchJson<TagListResponse>(this.url('/tags', params), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: options.signal,
    });
  }

  async getAllTags(
    accessToken: string,
    organizationId: string,
    options: {
      /** Batch window (useInfiniteList via batchFetcher). */
      limit?: number;
      offset?: number;
      cursor?: string;
      search: string;
      sortBy: 'id' | 'name' | 'slug' | 'description' | 'parentName' | 'tagType' | 'createdAt';
      sortDirection: 'asc' | 'desc';
      ids?: string[];
      signal?: AbortSignal;
    }
  ): Promise<TagListResponse> {
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({
      organizationId,
      search: options.search,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    });
    applyBatchWindow(params, options);
    if (options.ids?.length) params.set('ids', options.ids.join(','));

    const data = await this.fetchJson<{ tags?: Tag[]; pagination: TagListResponse['pagination'] }>(
      this.url('/tags/all', params),
      {
        method: 'GET',
        headers: this.authHeaders(accessToken),
        signal: options.signal,
      },
    );
    return {
      organizationId: '',
      tags: data.tags ?? [],
      pagination: data.pagination,
    };
  }

  async getTagTypes(accessToken: string, organizationId: string): Promise<string[]> {
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ tagTypes?: string[] }>(this.url('/tags/types', { organizationId }), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
    });
    return Array.isArray(data.tagTypes) ? data.tagTypes : [];
  }

  async createTag(
    organizationId: string,
    payload: {
      name: string;
      slug: string;
      description?: string | null;
      parentId?: string | null;
      tagType?: string;
    },
    accessToken: string,
  ): Promise<Tag> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(payload.name?.trim(), 'Tag name is required');
    requireArg(payload.slug?.trim(), 'Tag slug is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Tag>(this.url('/tags'), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        organizationId,
        ...payload,
      }),
    });
  }

  async updateTag(
    tagId: string,
    organizationId: string,
    updates: {
      name?: string;
      slug?: string;
      description?: string | null;
      parentId?: string | null;
      tagType?: string;
    },
    accessToken: string,
  ): Promise<Tag> {
    requireArg(tagId, 'Tag ID is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Tag>(this.url(`/tags/${encodeURIComponent(tagId)}`), {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        organizationId,
        ...updates,
      }),
    });
  }

  async deleteTag(tagId: string, organizationId: string, accessToken: string): Promise<void> {
    requireArg(tagId, 'Tag ID is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    await this.fetchJson<unknown>(this.url(`/tags/${encodeURIComponent(tagId)}`, { organizationId }), {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * Retrieve lenses for the caller organization.
   */
  async getLenses(organizationId: string, accessToken: string, signal?: AbortSignal): Promise<Lens[]> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ lenses?: Lens[] }>(this.url('/lenses', { organizationId }), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal,
    });
    return Array.isArray(data.lenses) ? data.lenses : [];
  }

  /**
   * One batch of the organization's lenses, searched server-side (name, alias).
   * The unbatched `getLenses` is the whole list, for bounded reads.
   */
  async getLensesBatch(
    organizationId: string,
    accessToken: string,
    options: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'createdAt';
      sortDirection?: 'asc' | 'desc';
      ids?: string[];
      /** Only the lenses that hold this template. */
      templateId?: string;
      signal?: AbortSignal;
    },
  ): Promise<{ lenses: Lens[]; pagination: import('../types/api').BatchPagination }> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({ organizationId });
    applyBatchWindow(params, options);
    if (options.search) params.set('search', options.search);
    if (options.sortBy) params.set('sortBy', options.sortBy);
    if (options.sortDirection) params.set('sortDirection', options.sortDirection);
    if (options.ids?.length) params.set('ids', options.ids.join(','));
    if (options.templateId) params.set('templateId', options.templateId);

    const data = await this.fetchJson<{ lenses?: Lens[]; pagination: import('../types/api').BatchPagination }>(
      this.url('/lenses', params),
      { method: 'GET', headers: this.authHeaders(accessToken), signal: options.signal },
    );
    return { lenses: Array.isArray(data.lenses) ? data.lenses : [], pagination: data.pagination };
  }

  /**
   * Create a new lens.
   */
  async createLens(
    payload: {
      name: string;
      alias: string;
      description?: string | null;
      processorTemplateIds?: string[];
      isPublic?: boolean;
    },
    organizationId: string,
    accessToken: string,
  ): Promise<Lens> {
    requireArg(payload.name?.trim(), 'Lens name is required');
    requireArg(payload.alias?.trim(), 'Lens alias is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Lens>(this.url('/lenses', { organizationId }), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ ...payload, organizationId }),
    });
  }

  /**
   * Update an existing lens. processorTemplateIds, when provided, replaces the full set.
   */
  async updateLens(
    id: string,
    updates: {
      name?: string;
      alias?: string;
      description?: string | null;
      processorTemplateIds?: string[];
      isPublic?: boolean;
    },
    organizationId: string,
    accessToken: string,
  ): Promise<Lens> {
    requireArg(id, 'Lens id is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<Lens>(this.url(`/lenses/${encodeURIComponent(id)}`, { organizationId }), {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ ...updates, organizationId }),
    });
  }

  /**
   * Delete a lens by id.
   */
  async deleteLens(id: string, organizationId: string, accessToken: string): Promise<void> {
    requireArg(id, 'Lens id is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    await this.fetchJson<unknown>(this.url(`/lenses/${encodeURIComponent(id)}`, { organizationId }), {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * Retrieve processor templates for the caller organization.
   */
  async getTemplateProcessors(
    organizationId: string,
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<TemplateProcessor[]> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ processors?: TemplateProcessor[] }>(
      this.url('/template-processors', { organizationId }),
      {
        method: 'GET',
        headers: this.authHeaders(accessToken),
        signal,
      },
    );
    return Array.isArray(data.processors) ? data.processors : [];
  }

  /**
   * Every template this organization may attach to one of its lenses: its own,
   * whatever their visibility, plus every template any organization has
   * published.
   *
   * Same route as the listing above with `scope=attachable`, so an older API
   * simply ignores the parameter and answers the ordinary listing — which is
   * why the response key differs and this reads `templates`, never `processors`.
   */
  async getAttachableTemplates(
    organizationId: string,
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<AttachableTemplate[]> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ templates?: AttachableTemplate[] }>(
      this.url('/template-processors', { organizationId, scope: 'attachable' }),
      {
        method: 'GET',
        headers: this.authHeaders(accessToken),
        signal,
      },
    );
    return Array.isArray(data.templates) ? data.templates : [];
  }

  /** One batch of attachable templates (own first, then name), searched server-side. */
  async getAttachableTemplatesBatch(
    organizationId: string,
    accessToken: string,
    options: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      ids?: string[];
      signal?: AbortSignal;
    },
  ): Promise<{ templates: AttachableTemplate[]; pagination: import('../types/api').BatchPagination }> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({ organizationId, scope: 'attachable' });
    applyBatchWindow(params, options);
    if (options.search) params.set('search', options.search);
    if (options.ids?.length) params.set('ids', options.ids.join(','));

    const data = await this.fetchJson<{
      templates?: AttachableTemplate[];
      pagination: import('../types/api').BatchPagination;
    }>(this.url('/template-processors', params), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: options.signal,
    });
    return { templates: Array.isArray(data.templates) ? data.templates : [], pagination: data.pagination };
  }

  /**
   * Create a new processor template.
   */
  async createTemplateProcessor(
    payload: {
      name: string;
      slug?: string;
      description?: string;
    },
    organizationId: string,
    accessToken: string,
  ): Promise<CreatedTemplateProcessor> {
    requireArg(payload.name?.trim(), 'Template name is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<CreatedTemplateProcessor>(this.url('/template-processors', { organizationId }), {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ ...payload, organizationId }),
    });
  }

  /**
   * Update processor template metadata by slug.
   */
  async updateTemplateProcessor(
    slug: string,
    updates: {
      name?: string;
      description?: string;
      isPublic?: boolean;
      processorTemplateVersionId?: string | null;
    },
    organizationId: string,
    accessToken: string,
  ): Promise<TemplateProcessor> {
    requireArg(slug, 'Template slug is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<TemplateProcessor>(
      this.url(`/template-processors/${encodeURIComponent(slug)}`, { organizationId }),
      {
        method: 'PUT',
        headers: this.authHeaders(accessToken),
        body: JSON.stringify({ ...updates, organizationId }),
      },
    );
  }

  /**
   * Retrieve template version history by template slug.
   */
  async getTemplateProcessorVersions(slug: string, organizationId: string, accessToken: string): Promise<TemplateProcessorVersion[]> {
    requireArg(slug, 'Template slug is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const data = await this.fetchJson<{ versions?: TemplateProcessorVersion[] }>(
      this.url(`/template-processors/${encodeURIComponent(slug)}/versions`, { organizationId }),
      {
        method: 'GET',
        headers: this.authHeaders(accessToken),
      },
    );
    return Array.isArray(data.versions) ? data.versions : [];
  }

  /**
   * Register a pushed image tag as a new template version.
   *
   * The API resolves the image digest from ECR *before* it writes anything, so
   * this fails with a 400 when the tag has not been pushed yet — which is why
   * the publish flow polls the version list rather than trusting a user's word
   * that the push happened.
   *
   * `supportedTags` is the set of event-type tag UUIDs this version emits.
   * Omitting it silently inherits the previous version's set (or `[]` when
   * there is no previous version), and an empty set can never match a
   * subscription — so the caller should always pass it explicitly.
   */
  async createTemplateProcessorVersion(
    slug: string,
    payload: {
      tag: string;
      version?: string;
      releaseNotes?: string;
      supportedTags?: string[];
      supportedSensorTypes?: string[];
      /**
       * The exact set of lenses the template should belong to, applied in the
       * same transaction as the version insert. Omit to leave routing alone —
       * the CLI and CI publishers never send it.
       */
      lensIds?: string[];
      /**
       * The version's environment variables. Omit to inherit the previous
       * version's; a list, even an empty one, replaces them. A secret sent with
       * no value keeps the saved one, read from `environmentFromVersionId`.
       */
      environment?: Array<{ name: string; value?: string; secret: boolean }>;
      environmentFromVersionId?: string;
      isActive?: boolean;
    },
    organizationId: string,
    accessToken: string,
  ): Promise<CreatedTemplateProcessorVersion> {
    requireArg(slug, 'Template slug is required');
    requireArg(payload.tag?.trim(), 'Image tag is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<CreatedTemplateProcessorVersion>(
      this.url(`/template-processors/${encodeURIComponent(slug)}/versions`, { organizationId }),
      {
        method: 'POST',
        headers: this.authHeaders(accessToken),
        body: JSON.stringify({ ...payload, organizationId }),
      },
    );
  }

  /**
   * Issue repository-scoped temporary push credentials by template slug.
   */
  async getTemplateProcessorPushCredentials(slug: string, organizationId: string, accessToken: string): Promise<TemplateProcessorPushCredentials> {
    requireArg(slug, 'Template slug is required');
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<TemplateProcessorPushCredentials>(
      this.url(`/template-processors/${encodeURIComponent(slug)}/registry/ecr-push-credentials`, { organizationId }),
      {
        method: 'POST',
        headers: this.authHeaders(accessToken),
      },
    );
  }

  /**
   * Retrieve users that belong to the given organization, including their role.
   */
  async getOrganizationUsers(
    organizationId: string,
    accessToken: string,
    options: {
      /** Batch window (useInfiniteList via batchFetcher). */
      limit?: number;
      offset?: number;
      cursor?: string;
      search: string;
      sortBy: 'name' | 'email' | 'firstName' | 'lastName' | 'role' | 'status' | 'joinedAt' | 'createdAt';
      sortDirection: 'asc' | 'desc';
      signal?: AbortSignal;
    },
  ): Promise<OrganizationUsersListResponse> {
    requireArg(organizationId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const params = new URLSearchParams({
      organizationId,
      search: options.search,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
    });
    applyBatchWindow(params, options);

    return this.fetchJson<OrganizationUsersListResponse>(this.url('/organizations/users', params), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: options.signal,
    });
  }

  /**
   * Retrieve authenticated portal user profile and organization memberships.
   */
  async getCurrentUser(accessToken: string): Promise<CurrentUserResponse> {
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson<CurrentUserResponse>(this.url('/portal/current-user'), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
    });
  }

  async checkEmailTaken(accessToken: string, email: string): Promise<boolean> {
    const data = await this.fetchJson<{ taken: boolean }>(this.url('/portal/check-email', { email }), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
    });
    return data.taken;
  }

  async updateUserProfile(accessToken: string, data: import('../types/api').UpdateProfileRequest): Promise<import('../types/api').CurrentUserResponse> {
    return this.fetchJson<import('../types/api').CurrentUserResponse>(this.url('/portal/me'), {
      method: 'PATCH',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(data),
    });
  }

  /**
   * The one place the base URL is joined to a path — the trailing-slash trim
   * used to be written out at every call site. Query parameters are optional
   * and an empty set never leaves a bare "?" behind.
   */
  private url(path: string, params?: URLSearchParams | Record<string, string>): string {
    const base = `${this.apiBaseUrl.replace(/\/$/, '')}${path}`;
    if (!params) {
      return base;
    }
    const query = (params instanceof URLSearchParams ? params : new URLSearchParams(params)).toString();
    return query ? `${base}?${query}` : base;
  }

  /**
   * The single request path for every method on this client: one timeout, one
   * error shape, one empty-body rule.
   *
   * A caller-supplied signal (useInfiniteList's per-request controller, a page's
   * organization-switch controller) is chained onto this request's own
   * controller, so whichever fires first wins and a caller abort still
   * surfaces as the AbortError those callers are written to swallow.
   */
  private async fetchJson<T>(url: string, options: RequestInit): Promise<T> {
    const { signal: callerSignal, ...init } = options;
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    const forwardAbort = () => controller.abort();
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort();
      } else {
        callerSignal.addEventListener('abort', forwardAbort);
      }
    }

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      if (!response.ok) {
        throw await this.errorFromResponse(response);
      }
      return await this.readJson<T>(response);
    } catch (err: unknown) {
      // fetch rejects with an AbortError either way; only this flag says which
      // controller pulled the trigger.
      if (timedOut) {
        throw new ApiTimeoutError(REQUEST_TIMEOUT_MS);
      }
      throw err;
    } finally {
      clearTimeout(timer);
      if (callerSignal) {
        callerSignal.removeEventListener('abort', forwardAbort);
      }
    }
  }

  /**
   * Deletes answer 204 (or 200 with no body) and `response.json()` throws on
   * an empty string. Callers that ignore the result must not get a parse error
   * for a request that succeeded.
   */
  private async readJson<T>(response: Response): Promise<T> {
    const text = await response.text();
    if (!text) {
      return undefined as unknown as T;
    }
    return JSON.parse(text) as T;
  }

  /**
   * Build the ApiError for a failed response. One definition of the message —
   * the server's `error` field when it sent JSON, the raw body otherwise, and
   * `HTTP error! status: <n>` as the last resort — so every call site produces
   * the wording humanizeError's patterns are written against.
   */
  private async errorFromResponse(response: Response): Promise<ApiError> {
    const errorText = await response.text();
    let errorData: { error?: string; code?: unknown } = {};
    try {
      const parsed: unknown = JSON.parse(errorText);
      // A body of `null` or `"a string"` parses without throwing; only an
      // object can carry the error field.
      if (parsed && typeof parsed === 'object') {
        errorData = parsed as { error?: string; code?: unknown };
      }
    } catch {
      // Not JSON — fall back to the raw text below.
    }
    return new ApiError(
      errorData.error || errorText || `HTTP error! status: ${response.status}`,
      response.status,
      typeof errorData.code === 'string' ? errorData.code : undefined,
    );
  }

  private authHeaders(accessToken: string): Record<string, string> {
    return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
  }

  async listOrgPermissions(orgId: string, accessToken: string, signal?: AbortSignal): Promise<Permission[]> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/permissions`);
    const data = await this.fetchJson<{ permissions: Permission[] }>(url, {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal,
    });
    return data.permissions ?? [];
  }

  async listOrgRoles(orgId: string, accessToken: string, signal?: AbortSignal): Promise<Role[]> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/roles`);
    const data = await this.fetchJson<{ roles: Role[] }>(url, {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal,
    });
    return data.roles ?? [];
  }

  async createOrgRole(
    orgId: string,
    payload: { name: string; description?: string | null; slug?: string | null; permissionIds: string[] },
    accessToken: string,
  ): Promise<Role> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/roles`);
    return this.fetchJson<Role>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(payload),
    });
  }

  async updateOrgRole(
    orgId: string,
    roleId: string,
    payload: { name: string; description?: string | null; slug?: string | null; permissionIds: string[] },
    accessToken: string,
  ): Promise<Role> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/roles/${encodeURIComponent(roleId)}`);
    return this.fetchJson<Role>(url, {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(payload),
    });
  }

  async deleteOrgRole(orgId: string, roleId: string, accessToken: string): Promise<void> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/roles/${encodeURIComponent(roleId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  async listUserRoles(orgId: string, userId: string, accessToken: string): Promise<UserRoleAssignment[]> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/roles`);
    const data = await this.fetchJson<{ assignments: UserRoleAssignment[] }>(url, {
      method: 'GET',
      headers: this.authHeaders(accessToken),
    });
    return data.assignments ?? [];
  }

  async assignUserRole(
    orgId: string,
    userId: string,
    roleId: string,
    accessToken: string,
  ): Promise<UserRoleAssignment[]> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/roles`);
    const data = await this.fetchJson<{ assignments: UserRoleAssignment[] }>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ roleId }),
    });
    return data.assignments ?? [];
  }

  async revokeUserRole(orgId: string, userId: string, roleId: string, accessToken: string): Promise<void> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  async inviteOrganizationMember(
    orgId: string,
    payload: InviteMemberRequest,
    accessToken: string,
  ): Promise<InviteMemberResponse> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/members`);
    return this.fetchJson<InviteMemberResponse>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(payload),
    });
  }

  async removeOrganizationMember(orgId: string, userId: string, accessToken: string): Promise<void> {
    const url = this.url(`/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * Re-sends the invitation email to a member who has not signed in yet. The
   * API answers 409 once the person has accepted or set a password.
   */
  async resendOrganizationInvitation(
    orgId: string,
    userId: string,
    accessToken: string,
  ): Promise<ResendInvitationResponse> {
    const url = this.url(
      `/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/resend-invitation`,
    );
    return this.fetchJson<ResendInvitationResponse>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
    });
  }

  // ---------------------------------------------------------------------------
  // System Admin API (system org only)
  // ---------------------------------------------------------------------------

  async listSystemOrgs(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'memberCount' | 'createdAt';
      sortDirection?: 'asc' | 'desc';
      ids?: string[];
      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemOrgsListResponse> {
    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.ids?.length) qs.set('ids', params.ids.join(','));
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/orgs`, qs);
    return this.fetchJson<import('../types/api').SystemOrgsListResponse>(url, {
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  async getSystemOrg(orgId: string, targetOrgId: string, accessToken: string): Promise<import('../types/api').SystemOrgDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/orgs/${encodeURIComponent(targetOrgId)}`);
    return this.fetchJson<import('../types/api').SystemOrgDetail>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * The tier vocabulary, in dropdown order.
   *
   * Fetched rather than hardcoded so a tier added straight to the database
   * appears in the System Admin dropdown with no portal change.
   */
  async listSystemTiers(orgId: string, accessToken: string): Promise<import('../types/api').SystemOrgsTiersResponse> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/tiers`);
    return this.fetchJson<import('../types/api').SystemOrgsTiersResponse>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  async updateSystemOrg(
    orgId: string,
    targetOrgId: string,
    updates: { name?: string; tier?: string },
    accessToken: string,
  ): Promise<import('../types/api').SystemOrgDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/orgs/${encodeURIComponent(targetOrgId)}`);
    return this.fetchJson<import('../types/api').SystemOrgDetail>(url, {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(updates),
    });
  }

  async deleteSystemOrg(orgId: string, targetOrgId: string, accessToken: string): Promise<void> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/orgs/${encodeURIComponent(targetOrgId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  async listSystemUsers(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'organizationCount' | 'lastLoginAt' | 'createdAt';
      sortDirection?: 'asc' | 'desc';

      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemUsersListResponse> {
    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);

    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/users`, qs);
    return this.fetchJson<import('../types/api').SystemUsersListResponse>(url, {
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  async getSystemUser(orgId: string, userId: string, accessToken: string): Promise<import('../types/api').SystemUserDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}`);
    return this.fetchJson<import('../types/api').SystemUserDetail>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  async deleteSystemUser(orgId: string, userId: string, accessToken: string): Promise<void> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  async listSystemProcessorTemplates(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'createdAt';
      sortDirection?: 'asc' | 'desc';
      /**
       * Which slice to show. Omitted or 'all' returns everything — the review
       * queue is a filter over this one list rather than a separate route, so
       * search and sort survive a filter change.
       */
      reviewFilter?: import('../types/api').TemplateReviewFilter;
      /** Own-or-public templates of this organization, own first then name (the lens form picker). */
      attachableFor?: string;
      /** Only templates this organization publishes; also scopes filterCounts. */
      organizationId?: string;
      ids?: string[];
      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemProcessorTemplatesListResponse> {
    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.reviewFilter && params.reviewFilter !== 'all') qs.set('reviewFilter', params.reviewFilter);
    if (params.attachableFor) qs.set('attachableFor', params.attachableFor);
    if (params.organizationId) qs.set('organizationId', params.organizationId);
    if (params.ids?.length) qs.set('ids', params.ids.join(','));
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/processor-templates`, qs);
    return this.fetchJson<import('../types/api').SystemProcessorTemplatesListResponse>(url, {
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  async listSystemSensors(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
      /** Only sensors this organization owns. */
      organizationId?: string;
      /** Only public or only private sensors; omitted = both. */
      visibility?: 'public' | 'private';
      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<{ sensors: import('../types/api').SystemSensor[]; pagination: import('../types/api').BatchPagination }> {
    requireArg(orgId, 'Organization ID is required');
    requireArg(accessToken, 'Access token is required');

    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.organizationId) qs.set('organizationId', params.organizationId);
    if (params.visibility) qs.set('visibility', params.visibility);

    return this.fetchJson(this.url(`/system/org/${encodeURIComponent(orgId)}/sensors`, qs), {
      method: 'GET',
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  /** Changes one sensor's visibility. Visibility is the only field this route writes. */
  async updateSystemSensor(
    orgId: string,
    sensorId: string,
    isPublic: boolean,
    accessToken: string,
  ): Promise<import('../types/api').SystemSensor> {
    requireArg(orgId, 'Organization ID is required');
    requireArg(sensorId, 'Sensor ID is required');
    requireArg(accessToken, 'Access token is required');

    return this.fetchJson(
      this.url(`/system/org/${encodeURIComponent(orgId)}/sensors/${encodeURIComponent(sensorId)}`),
      {
        method: 'PUT',
        headers: this.authHeaders(accessToken),
        body: JSON.stringify({ isPublic }),
      },
    );
  }

  async getSystemProcessorTemplate(orgId: string, templateId: string, accessToken: string): Promise<import('../types/api').SystemProcessorTemplateDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/processor-templates/${encodeURIComponent(templateId)}`);
    return this.fetchJson<import('../types/api').SystemProcessorTemplateDetail>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  /**
   * Records an administrator's decision on one template version.
   *
   * Approving makes the version live in the same call. `notes` is HTML from the
   * rich-text editor; the API sanitizes it again on arrival, so what is stored
   * is never what a client chose to send.
   */
  async reviewSystemProcessorTemplateVersion(
    orgId: string,
    templateId: string,
    versionId: string,
    body: { decision: import('../types/api').TemplateReviewDecision; notes?: string | null },
    accessToken: string,
  ): Promise<import('../types/api').TemplateVersionReviewResponse> {
    const url = this.url(
      `/system/org/${encodeURIComponent(orgId)}/processor-templates/${encodeURIComponent(templateId)}`
        + `/versions/${encodeURIComponent(versionId)}/review`,
    );
    return this.fetchJson<import('../types/api').TemplateVersionReviewResponse>(url, {
      method: 'POST',
      headers: { ...this.authHeaders(accessToken), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // --- System-admin lens management -------------------------------------

  async listSystemLenses(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'createdAt';
      sortDirection?: 'asc' | 'desc';
      organizationId?: string;
      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemLensesListResponse> {
    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.organizationId) qs.set('organizationId', params.organizationId);
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/lenses`, qs);
    return this.fetchJson<import('../types/api').SystemLensesListResponse>(url, {
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  async getSystemLens(orgId: string, lensId: string, accessToken: string): Promise<import('../types/api').SystemLensDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/lenses/${encodeURIComponent(lensId)}`);
    return this.fetchJson<import('../types/api').SystemLensDetail>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  async createSystemLens(
    orgId: string,
    payload: {
      organizationId: string;
      name: string;
      alias: string;
      description?: string | null;
      isPublic?: boolean;
      processorTemplateIds?: string[];
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemLensDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/lenses`);
    return this.fetchJson<import('../types/api').SystemLensDetail>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(payload),
    });
  }

  async updateSystemLens(
    orgId: string,
    lensId: string,
    updates: {
      name?: string;
      alias?: string;
      description?: string | null;
      isPublic?: boolean;
      processorTemplateIds?: string[];
    },
    accessToken: string,
  ): Promise<import('../types/api').SystemLensDetail> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/lenses/${encodeURIComponent(lensId)}`);
    return this.fetchJson<import('../types/api').SystemLensDetail>(url, {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(updates),
    });
  }

  async deleteSystemLens(orgId: string, lensId: string, accessToken: string): Promise<void> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/lenses/${encodeURIComponent(lensId)}`);
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

  // --- System-admin license keys -----------------------------------------

  async listSystemLicenseKeys(
    orgId: string,
    params: {
      limit?: number;
      offset?: number;
      cursor?: string;
      search?: string;
      sortBy?: 'name' | 'status' | 'usedCount' | 'expiresAt' | 'createdAt';
      sortDirection?: 'asc' | 'desc';
      status?: 'all' | 'active' | 'expired';
      signal?: AbortSignal;
    },
    accessToken: string,
  ): Promise<import('../types/api').LicenseKeysListResponse> {
    const qs = new URLSearchParams();
    applyBatchWindow(qs, params);
    if (params.search) qs.set('search', params.search);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.status && params.status !== 'all') qs.set('status', params.status);
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/license-keys`, qs);
    return this.fetchJson<import('../types/api').LicenseKeysListResponse>(url, {
      headers: this.authHeaders(accessToken),
      signal: params.signal,
    });
  }

  async getSystemLicenseKey(
    orgId: string,
    licenseKeyId: string,
    accessToken: string,
  ): Promise<import('../types/api').LicenseKey> {
    const url = this.url(
      `/system/org/${encodeURIComponent(orgId)}/license-keys/${encodeURIComponent(licenseKeyId)}`,
    );
    return this.fetchJson<import('../types/api').LicenseKey>(url, {
      headers: this.authHeaders(accessToken),
    });
  }

  /** The value is generated server-side; there is deliberately no way to choose it. */
  async createSystemLicenseKey(
    orgId: string,
    payload: {
      name: string;
      description?: string | null;
      maxUses?: number | null;
      expiresAt?: string | null;
    },
    accessToken: string,
  ): Promise<import('../types/api').LicenseKey> {
    const url = this.url(`/system/org/${encodeURIComponent(orgId)}/license-keys`);
    return this.fetchJson<import('../types/api').LicenseKey>(url, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(payload),
    });
  }

  async updateSystemLicenseKey(
    orgId: string,
    licenseKeyId: string,
    updates: {
      name?: string;
      description?: string | null;
      maxUses?: number | null;
      expiresAt?: string | null;
      isExpired?: boolean;
    },
    accessToken: string,
  ): Promise<import('../types/api').LicenseKey> {
    const url = this.url(
      `/system/org/${encodeURIComponent(orgId)}/license-keys/${encodeURIComponent(licenseKeyId)}`,
    );
    return this.fetchJson<import('../types/api').LicenseKey>(url, {
      method: 'PUT',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify(updates),
    });
  }

  async deleteSystemLicenseKey(orgId: string, licenseKeyId: string, accessToken: string): Promise<void> {
    const url = this.url(
      `/system/org/${encodeURIComponent(orgId)}/license-keys/${encodeURIComponent(licenseKeyId)}`,
    );
    await this.fetchJson<unknown>(url, {
      method: 'DELETE',
      headers: this.authHeaders(accessToken),
    });
  }

}


// Export singleton instance
export const apiService = ApiService.getInstance();
